// import { BulbOutlined } from "@ant-design/icons";
import { MenuUnfoldOutlined } from "@ant-design/icons";
import { Alert, App as AntApp, Button, ConfigProvider, Tooltip } from "antd";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
// import { useMemo } from "react";

import GraphCanvas from "@/warRoom/canvas/GraphCanvas";
import Toolbox from "@/warRoom/components/Toolbox";
import { downloadBlob, surfaceToBlob } from "@/warRoom/canvas/exportImage";
// import { datasetByKey } from "@/warRoom/datasets/registry";
import { tr } from "@/warRoom/lib/i18n";
import { sessionCompanyId } from "@/warRoom/store/remote";
import { useGraphStore } from "@/warRoom/store/useGraphStore";
import { CHROME } from "@/warRoom/theme/palette";
import { antdTheme } from "@/warRoom/theme/antdTheme";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import "@/warRoom/warRoom.css";

const COMPACT_W = 1000;
const OVERLAY_W = 900;

function WarRoomStudio() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  // const widgetCount = useGraphStore((s) => s.widgets.length);
  const error = useGraphStore((s) => s.error);
  const mode = useGraphStore((s) => s.mode);
  // const setMode = useGraphStore((s) => s.setMode);

  const surfaceRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const roomRef = useRef<HTMLDivElement>(null);
  const [roomW, setRoomW] = useState(0);
  const [toolboxOpen, setToolboxOpen] = useState(true);
  const decided = useRef(false);
  useLayoutEffect(() => {
    const el = roomRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (!w) return;
      setRoomW(w);
      if (!decided.current) {
        decided.current = true;
        if (w < OVERLAY_W) setToolboxOpen(false);
      }
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const compact = roomW > 0 && roomW < COMPACT_W;
  const overlay = roomW > 0 && roomW < OVERLAY_W;

  const loadRoles = useGraphStore((s) => s.loadRoles);
  const hydrate = useGraphStore((s) => s.hydrate);
  const syncRemote = useGraphStore((s) => s.syncRemote);
  useEffect(() => {
    let alive = true;
    void loadRoles();
    // 先用這台瀏覽器的存檔立刻畫出來，再跟後端 employee.warRoomLayout 對一次（見 warRoom/store/persist.ts）
    void sessionCompanyId().then(async (companyId) => {
      if (!alive) return;
      const restored = hydrate(companyId ?? undefined);
      if (restored > 0)
        message.info(tr("warRoom.page.restored", { count: restored }));
      const synced = await syncRemote();
      if (alive && synced)
        message.info(tr("warRoom.page.synced", { count: synced }));
    });
    return () => {
      alive = false;
    };
  }, [loadRoles, hydrate, syncRemote, message]);

  // const widgets = useGraphStore((s) => s.widgets);
  // const byDataset = useMemo(() => {
  //   const counts = new Map<string, number>();
  //   for (const w of widgets) {
  //     const key = w.spec.datasetKey;
  //     if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  //   }
  //   return [...counts].map(
  //     ([key, n]) => `${datasetByKey(key)?.label ?? key} ${n}`,
  //   );
  // }, [widgets]);

  const exportPng = async () => {
    const surface = surfaceRef.current;
    if (!surface) return;
    setExporting(true);
    try {
      const blob = await surfaceToBlob(surface, {
        background: CHROME[mode].canvas,
      });
      downloadBlob(
        blob,
        t("warRoom.page.exportFileName", {
          date: dayjs().format("YYYYMMDD-HHmm"),
        }),
      );
      message.success(t("warRoom.page.exported"));
    } catch (e) {
      message.error(
        e instanceof Error ? e.message : t("warRoom.page.exportFailed"),
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      ref={roomRef}
      className={`war-room${compact ? " is-compact" : ""}${overlay ? " is-overlay" : ""}`}
      data-theme={mode}
    >
      {/* 頁首留著撐版面；清空畫布已移到工具箱標題列，跟輸出畫布放一起 */}
      <div className="war-room__head">
        <div className="war-room__actions" />
      </div>
      <div className="war-room__panel">
        {!toolboxOpen || overlay ? (
          <div className="war-room__rail">
            <Tooltip title={t("warRoom.page.expandToolbox")} placement="right">
              <Button
                type="text"
                aria-label={t("warRoom.page.expandToolbox")}
                icon={<MenuUnfoldOutlined />}
                onClick={() => setToolboxOpen(true)}
              />
            </Tooltip>
          </div>
        ) : null}
        <div className={`war-room__sider${toolboxOpen ? "" : " is-collapsed"}`}>
          <Toolbox
            exporting={exporting}
            onExport={() => void exportPng()}
            onCollapse={() => setToolboxOpen(false)}
          />
        </div>
        {overlay && toolboxOpen ? (
          <div
            className="war-room__scrim"
            aria-hidden
            onPointerDown={() => setToolboxOpen(false)}
          />
        ) : null}
        <div className="war-room__content">
          <div className="studio">
            {error ? (
              <Alert
                type="warning"
                showIcon
                closable
                style={{ marginBottom: 0 }}
                message={error}
              />
            ) : null}
            <GraphCanvas surfaceRef={surfaceRef} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WarRoomPage() {
  const mode = useGraphStore((s) => s.mode);

  return (
    <ConfigProvider theme={antdTheme(mode)} button={{ autoInsertSpace: false }}>
      <AntApp style={{ height: "100%" }}>
        <WarRoomStudio />
      </AntApp>
    </ConfigProvider>
  );
}
